import { useEffect } from "react";
import { Link } from "react-router";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollingDown } from "@/hooks/use-scrolling-down";
import { useTyping } from "@/hooks/use-typing";
import { cn } from "@/lib/utils";

/**
 * A page's way to make something — New space, Log a workout, New event — a
 * pill with a + and what it makes, floating at the bottom right of the page's
 * column, 16px above the tab bar (0079). While the page scrolls down it shrinks
 * to its +, its words still heard, and opens as it scrolls back up. It
 * leaves room under the
 * page so the last card is never under it, lifts toasts above it, and steps
 * aside while somebody types, as the tab bar does (0071).
 */
export function CreateButton({
  label,
  ...action
}: { label: string } & ({ to: string; onClick?: never } | { onClick: () => void; to?: never })) {
  const typing = useTyping();
  const shrunk = useScrollingDown();
  useEffect(() => {
    document.documentElement.style.setProperty("--create-button", "4.5rem");
    return () => {
      document.documentElement.style.removeProperty("--create-button");
    };
  }, []);

  const look = cn(
    "pointer-events-auto h-14 rounded-full text-base shadow-lg motion-reduce:transition-none [&_svg:not([class*='size-'])]:size-5",
    shrunk ? "gap-0 px-[1.0625rem]" : "gap-2 px-6",
  );
  // Narrowed rather than taken away, so a screen reader still hears it.
  const words = (
    <span
      className={cn(
        "overflow-hidden transition-[max-width,opacity] duration-200 motion-reduce:transition-none",
        shrunk ? "max-w-0 opacity-0" : "max-w-48 opacity-100",
      )}
    >
      {label}
    </span>
  );
  const inside = (
    <>
      <Plus aria-hidden="true" />
      {words}
    </>
  );
  return (
    <>
      <div aria-hidden="true" className="h-10" />
      <div
        hidden={typing}
        className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--tab-bar-top)+1rem)] z-20"
      >
        <div className="mx-auto flex max-w-3xl justify-end px-4">
          <Button
            asChild={action.to !== undefined}
            type={action.to === undefined ? "button" : undefined}
            className={look}
            data-shrunk={shrunk || undefined}
            onClick={action.onClick}
          >
            {action.to !== undefined ? <Link to={action.to}>{inside}</Link> : inside}
          </Button>
        </div>
      </div>
    </>
  );
}
